import { useMemo } from 'react';
import React from 'react';
import { useQuery } from 'react-query';
import { BankAccountTypes, FixtureCounterpartyTypes, FixtureTypes } from '@shared/utils/constants';
import isBankAccountAvailableForFixingCompany from '@shared/utils/isBankAccountAvailableForFixingCompany';
import { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import { TcFixtureProps } from '@api/models/tc-fixture';
import TcTerms, { TcTermsProps } from '@api/models/tc-terms';
import getTcEstimateTermsItems from '@client/screens/estimates/details/helpers/getTcEstimateTermsItems';
import { getCounterparties, getOrgBankAccounts, getPortsAndRanges, getVesselBankAccounts, getVessels } from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import EditableDetails from './EditableDetails/EditableDetails';

const TcTermsDetails = ({
	fixtureDetails,
	tcTerms,
	onUpdate,
	locked = false,
	hasInvoices = false,
}: {
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps>;
	tcTerms: TcTerms;
	onUpdate: (attributes: Partial<TcTermsProps>) => void;
	locked?: boolean;
	hasInvoices?: boolean;
}) => {
	const fixtureCurrency = fixtureDetails?.currency;

	const {
		data: charterers,
	} = useQuery(
		'charterers',
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);

	const {
		data: vessels,
	} = useQuery(
		'vessels',
		() => getVessels(),
	);

	const {
		data: portsAndRanges,
	} = useQuery(
		'portsAndRanges',
		() => getPortsAndRanges(),
	);

	const { data: bankAccounts = [] } = useQuery(
		['bankAccounts', fixtureDetails?.vesselId, fixtureDetails?.type, vessels, tcTerms?.fixingCompanyId],
		async () => {
			if (!fixtureDetails?.vesselId || !vessels?.length) {
				return [];
			}

			const bankAccountType =
				fixtureDetails.type === FixtureTypes.TC_IN ?
					BankAccountTypes.TC_IN :
					BankAccountTypes.OURS;

			const [vesselAccounts, orgAccounts] = await Promise.all([
				getVesselBankAccounts(tcTerms.vesselId!, bankAccountType),
				getOrgBankAccounts(true),
			]);

			const accounts = [...vesselAccounts, ...orgAccounts];
			const companyAccounts = fixtureDetails.type === FixtureTypes.TC_IN ?
				accounts :
				accounts.filter((a) => isBankAccountAvailableForFixingCompany(a, tcTerms?.fixingCompanyId));

			return companyAccounts.filter((a) => a.currency === fixtureCurrency);
		},
		{
			enabled: Boolean(tcTerms?.vesselId && vessels?.length),
		},
	);

	const { userInfo } = useAuth();

	const items = useMemo(() => {
		if (fixtureDetails == null || tcTerms == null) {
			return [];
		}

		return getTcEstimateTermsItems({
			tcTerms,
			fixtureDetails,
			charterers,
			fixingCompanies: userInfo.fixingCompanies,
			vessels,
			fixtureCurrency,
			portsAndRanges,
			bankAccounts,
			onUpdate,
			hasInvoices,
		});
	}, [
		fixtureDetails,
		charterers,
		userInfo.fixingCompanies,
		vessels,
		fixtureCurrency,
		portsAndRanges,
		bankAccounts,
		tcTerms,
		onUpdate,
		hasInvoices
	]);

	return (
		<EditableDetails
			title="Details"
			// @ts-ignore this shit never works
			items={items}
			onEditValuesChange={onUpdate}
			editing={!locked}
			hideHeader
		/>
	);
};

export default TcTermsDetails;
